export interface UnpickedItem {
  label_name?: string
  small_qty?: number | string
  large_qty?: number | string
  quantity_remaining?: number | string
}

interface PreparedRow {
  index: number
  product: string
  small: number
  large: number
  total: number
}

// ─── Page geometry (US Letter, points) ──────────────────────────────────────
const PAGE_W = 612
const PAGE_H = 792
const MARGIN = 50
const RIGHT = PAGE_W - MARGIN // 562
const ROW_H = 22
const BOTTOM_LIMIT = 60

// Column x positions
const X_NUM = MARGIN + 4
const X_PRODUCT = MARGIN + 32
const X_SMALL = MARGIN + 340
const X_LARGE = MARGIN + 420
const X_TOTAL = MARGIN + 485

// ─── helpers ─────────────────────────────────────────────────────────────────

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

/** Escape a text string for a PDF literal and drop chars outside WinAnsi (>255). */
function pdfText(s: string): string {
  let out = ''
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0
    if (code > 255) {
      out += '?'
      continue
    }
    const c = String.fromCharCode(code)
    if (c === '\\') out += '\\\\'
    else if (c === '(') out += '\\('
    else if (c === ')') out += '\\)'
    else out += c
  }
  return out
}

// ─── content-stream operator builders ────────────────────────────────────────

function text(x: number, y: number, size: number, font: 'F1' | 'F2', value: string): string {
  return `BT /${font} ${num(size)} Tf 1 0 0 1 ${num(x)} ${num(y)} Tm (${pdfText(value)}) Tj ET\n`
}

function line(x1: number, y1: number, x2: number, y2: number, width = 1, gray = 0): string {
  return `${num(width)} w ${num(gray)} ${num(gray)} ${num(gray)} RG ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S\n`
}

function strokeRect(x: number, y: number, w: number, h: number, width = 1): string {
  return `${num(width)} w 0 0 0 RG ${num(x)} ${num(y)} ${num(w)} ${num(h)} re S\n`
}

// ─── PDF assembly ────────────────────────────────────────────────────────────

function buildPdf(pageContents: string[]): ArrayBuffer {
  const pageCount = pageContents.length
  const kids: string[] = []
  for (let i = 0; i < pageCount; i++) kids.push(`${5 + 2 * i} 0 R`)

  const objects: { num: number; body: string }[] = []
  objects.push({ num: 1, body: '<< /Type /Catalog /Pages 2 0 R >>' })
  objects.push({ num: 2, body: `<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${pageCount} >>` })
  objects.push({ num: 3, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>' })
  objects.push({ num: 4, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>' })

  for (let i = 0; i < pageCount; i++) {
    const pageObj = 5 + 2 * i
    const contentObj = 6 + 2 * i
    objects.push({
      num: pageObj,
      body: `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObj} 0 R >>`,
    })
    const content = pageContents[i] ?? ''
    objects.push({
      num: contentObj,
      body: `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    })
  }

  objects.sort((a, b) => a.num - b.num)

  let pdf = '%PDF-1.4\n'
  const offsets: number[] = []
  for (const obj of objects) {
    offsets[obj.num] = pdf.length
    pdf += `${obj.num} 0 obj\n${obj.body}\nendobj\n`
  }

  const xrefOffset = pdf.length
  const total = objects.length + 1
  pdf += `xref\n0 ${total}\r\n`
  pdf += '0000000000 65535 f\r\n'
  for (let n = 1; n < total; n++) {
    pdf += `${String(offsets[n] ?? 0).padStart(10, '0')} 00000 n\r\n`
  }
  pdf += `trailer\n<< /Size ${total} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  const buffer = new ArrayBuffer(pdf.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff
  return buffer
}

// ─── table drawing ────────────────────────────────────────────────────────────

/** Returns the rowTop after drawing the repeating column header at `top`. */
function drawColumnHeader(ops: string[], top: number): number {
  ops.push(line(MARGIN, top, RIGHT, top, 1.2))
  const baseline = top - 14
  ops.push(text(X_NUM, baseline, 10, 'F2', '#'))
  ops.push(text(X_PRODUCT, baseline, 10, 'F2', 'Label Name'))
  ops.push(text(X_SMALL, baseline, 10, 'F2', 'Small'))
  ops.push(text(X_LARGE, baseline, 10, 'F2', 'Large'))
  ops.push(text(X_TOTAL, baseline, 10, 'F2', 'Total'))
  const ruleY = top - 20
  ops.push(line(MARGIN, ruleY, RIGHT, ruleY, 1.2))
  return ruleY - 2
}

/**
 * Builds a consolidated "items still to pick" PDF grouped by label name and
 * opens it in a new tab.
 */
export async function generateUnpickedItemsPdf(items: UnpickedItem[]): Promise<void> {
  const rows: PreparedRow[] = items.map((item, i) => {
    const small = Number(item.small_qty ?? 0)
    const large = Number(item.large_qty ?? 0)
    return {
      index: i + 1,
      product: item.label_name?.trim() || '\u2014',
      small,
      large,
      total: Number(item.quantity_remaining ?? small + large),
    }
  })

  const totalSmall = rows.reduce((s, r) => s + r.small, 0)
  const totalLarge = rows.reduce((s, r) => s + r.large, 0)
  const totalQty = rows.reduce((s, r) => s + r.total, 0)
  const generatedAt = new Date().toLocaleString()

  const pageContents: string[] = []
  let ops: string[] = ['0 0 0 rg\n0 0 0 RG\n']

  const flushPage = () => {
    pageContents.push(ops.join(''))
    ops = ['0 0 0 rg\n0 0 0 RG\n']
  }

  // ── Header ───────────────────────────────────────────────────────────────────
  ops.push(text(MARGIN, PAGE_H - MARGIN - 6, 24, 'F2', 'Items To Pick'))
  ops.push(text(MARGIN, PAGE_H - MARGIN - 28, 11, 'F1', 'All ordered items not yet picked, grouped by label name'))
  ops.push(text(MARGIN, PAGE_H - MARGIN - 46, 10, 'F1', `Generated ${generatedAt}`))
  ops.push(text(MARGIN, PAGE_H - MARGIN - 62, 10, 'F1', `${rows.length} label(s)`))

  // ── Table ───────────────────────────────────────────────────────────────────
  let rowTop = drawColumnHeader(ops, PAGE_H - MARGIN - 80)

  if (rows.length === 0) {
    ops.push(text(X_PRODUCT, rowTop - 16, 11, 'F1', 'Nothing left to pick \u2014 all items are picked.'))
  }

  for (const row of rows) {
    if (rowTop - ROW_H < BOTTOM_LIMIT) {
      flushPage()
      rowTop = drawColumnHeader(ops, PAGE_H - MARGIN)
    }
    const baseline = rowTop - 15
    ops.push(text(X_NUM, baseline, 11, 'F1', String(row.index)))
    const product = row.product.length > 48 ? `${row.product.slice(0, 46)}...` : row.product
    ops.push(text(X_PRODUCT, baseline, 11, 'F1', product))
    ops.push(text(X_SMALL, baseline, 11, 'F1', String(row.small)))
    ops.push(text(X_LARGE, baseline, 11, 'F1', String(row.large)))
    ops.push(text(X_TOTAL, baseline, 11, 'F2', String(row.total)))
    rowTop -= ROW_H
    ops.push(line(MARGIN, rowTop, RIGHT, rowTop, 0.5, 0.82))
  }

  // ── Total ──────────────────────────────────────────────────────────────────
  if (rowTop - 50 < BOTTOM_LIMIT) {
    flushPage()
    rowTop = PAGE_H - MARGIN
  }
  const ty = rowTop - 22
  const labelX = MARGIN + 4
  ops.push(line(MARGIN, ty + 12, RIGHT, ty + 12, 1.2))
  ops.push(text(labelX, ty - 4, 14, 'F2', 'Total To Pick'))
  ops.push(text(X_SMALL, ty - 4, 14, 'F2', String(totalSmall)))
  ops.push(text(X_LARGE, ty - 4, 14, 'F2', String(totalLarge)))
  ops.push(text(X_TOTAL, ty - 4, 14, 'F2', String(totalQty)))
  ops.push(strokeRect(MARGIN - 6, ty - 10, RIGHT - MARGIN + 12, 26, 1.2))

  flushPage()

  // ── Emit & open in a new tab ────────────────────────────────────────────────
  const bytes = buildPdf(pageContents)
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
